import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

export const useClaimOrderSubmitFormSchema = () => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimOrderPage.submitModal.errors"
  });

  const schema = useMemo(
    () =>
      z.object({
        invoiceNumber: z
          .string()
          .min(1, { message: t("invoiceNumberRequired") }),
        invoiceFile: z
          .union([z.instanceof(File), z.string(), z.null()])
          .superRefine((file, ctx) => {
            if (file == null || file === "") {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("invoiceFileRequired")
              });
              return z.NEVER;
            }
            if (file instanceof File) {
              if (file.type !== "application/pdf") {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t("invoiceFilePdfOnly")
                });
                return z.NEVER;
              }
              if (file.size > MAX_FILE_SIZE) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t("invoiceFileSize")
                });
              }
            }
          }),
        invoiceAmount: z
          .union([z.number(), z.string()])
          .superRefine((val, ctx) => {
            if (typeof val !== "number") {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("invoiceAmountRequired")
              });
            }
          }),
        invoiceDate: z.date({
          required_error: t("invoiceDateRequired"),
          invalid_type_error: t("invoiceDateRequired")
        }),
        taxInvoiceNumber: z
          .string()
          .min(1, { message: t("taxInvoiceNumberRequired") }),
        bankAccountId: z
          .string()
          .nullable()
          .superRefine((val, ctx) => {
            if (val === null) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("bankAccountRequired")
              });
            }
          })
      }),
    [t]
  );

  return schema;
};

export type ClaimOrderSubmitFormSchema = z.infer<
  ReturnType<typeof useClaimOrderSubmitFormSchema>
>;
